import { Head, Link, usePage } from "@inertiajs/react";
import { useState } from "react";

import { ProductCreationConfirmationDialog } from "@/components/product-creation-confirmation-dialog";
import { PublicCatalogBanner } from "@/components/public-catalog-banner";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import type {
  BreadcrumbItem,
  PagyMetadata,
  Product,
  RansackFilters,
  SharedData,
} from "@/types";

import { ProductsDataTable } from "./data-table";

interface ProductsProps {
  data: {
    products: Product[];
    pagy: PagyMetadata;
  };
  filters: RansackFilters;
  public_catalog_enabled: boolean;
}

export default function Products({
  data,
  filters,
  public_catalog_enabled,
}: ProductsProps) {
  const { products, pagy } = data;
  const { auth } = usePage<SharedData>().props;
  const routes = useOrganizationRoutes();
  const productsHref = routes.products.index();
  const importHref = routes.products.importNew();
  const organizationSlug = auth.current_organization?.slug ?? "";
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Productos",
      href: productsHref,
    },
  ];
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Productos" />
      <div className="flex h-full flex-1 flex-col gap-4 p-4">
        <PublicCatalogBanner
          enabled={public_catalog_enabled}
          organizationSlug={organizationSlug}
        />

        <div className="flex flex-col space-y-2">
          <h2 className="text-2xl font-bold">Productos</h2>
          <p className="text-muted-foreground">
            Gestiona el catálogo de productos y sus variantes
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Total: {pagy.count} productos
          </div>
          <ButtonGroup>
            <Button variant={"outline"} onClick={() => setIsDialogOpen(true)}>
              Crear Producto
            </Button>
            <Button asChild>
              <Link href={importHref}>Importar Excel</Link>
            </Button>
          </ButtonGroup>
        </div>

        <ProductsDataTable
          products={products}
          pagy={pagy}
          filters={filters}
          organizationSlug={organizationSlug}
        />
      </div>

      <ProductCreationConfirmationDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </AppLayout>
  );
}
